import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';
import { defaultHRM2040_R01Dto, HRM2040_R01Dto } from './Dto_HRM2040_R01';
import { formatLocalYearMonth } from 'src/utils/date/dateUtils';

/** 월근태 목록 조회와 재집계에 사용하는 조건이다. */
export interface HRM2040_R01Filter {
    workYm: string; // YYYYMM 근무년월
    compCd: string; // 사원 마스터 사업장. 빈 문자열이면 전체
    orgCd: string; // 사원 마스터 부서. 빈 문자열이면 전체
}

/** 현재 월을 기준으로 시작하는 검색 조건이다. */
export const defaultHRM2040_R01Filter: HRM2040_R01Filter = {
    workYm: formatLocalYearMonth(new Date()),
    compCd: '',
    orgCd: '',
};

/** 월근태 행, 직전 조회조건, 선택 및 그리드 표시 상태이다. */
export interface Store_HRM2040_R01 {
    HRM2040_R01Filter: HRM2040_R01Filter; // 화면에 보이는 현재 조회조건
    HRM2040_R01PastFilter: HRM2040_R01Filter | null; // 성공한 직전 조회조건. null이면 조회 전
    setHRM2040_R01Filter: (filter: Partial<HRM2040_R01Filter>) => void; // 현재 조회조건 갱신
    setHRM2040_R01PastFilter: (filter: HRM2040_R01Filter | null) => void; // 직전 조회조건 갱신
    HRM2040_R01List: HRM2040_R01Dto[]; // 편집 가능한 서버 목록
    setHRM2040_R01List: (list: HRM2040_R01Dto[]) => void; // 목록과 행 식별값 갱신
    updateHRM2040_R01List: (index: number, data: HRM2040_R01Dto) => void; // 한 행의 편집 결과 저장
    originalHRM2040_R01List: HRM2040_R01Dto[]; // 최근 조회의 변경 비교용 원본
    setOriginalHRM2040_R01List: (list: HRM2040_R01Dto[]) => void; // 원본 목록 갱신
    selectedHRM2040_R01List: string[]; // String(valueIndex) 선택 식별값
    setSelectedHRM2040_R01List: (ids: string[]) => void; // 선택 목록 전체 교체
    addSelectedHRM2040_R01List: (ids: string[]) => void; // 선택 식별값 추가
    removeSelectedHRM2040_R01List: (ids: string[]) => void; // 선택 식별값 제거
    initSelectedHRM2040_R01List: () => void; // 선택 해제
    allSelectedHRM2040_R01List: () => void; // 전체 선택
    columnState: ColumnState[] | null; // 저장한 컬럼 배치
    setColumnState: (state: ColumnState[] | null) => void; // 컬럼 배치 갱신
    scrollTop: number | null; // 저장한 스크롤 위치
    setScrollTop: (top: number | null) => void; // 스크롤 위치 갱신
    initData: () => void; // 화면 전용 상태 초기화
}

/** 월근태 편집 상태를 보관한다. API 호출은 Content에서 처리한다. */
export const useHRM2040_R01Store = create<Store_HRM2040_R01>((set) => ({
    HRM2040_R01Filter: { ...defaultHRM2040_R01Filter }, // 현재 검색조건의 복사본
    HRM2040_R01PastFilter: null, // 조회 전에는 재집계·Excel을 차단한다.
    /** 전달된 검색 조건 필드만 현재 값에 합친다. */
    setHRM2040_R01Filter: filter => set(state => ({
        HRM2040_R01Filter: { ...state.HRM2040_R01Filter, ...filter }, // 기존 조건 위에 전달 필드만 덮어쓴다.
    })),
    /** 마지막으로 성공한 조회조건을 복사해 저장한다. */
    setHRM2040_R01PastFilter: filter => set({
        HRM2040_R01PastFilter: filter ? { ...filter } : null, // null은 조회 전 상태이다.
    }),
    HRM2040_R01List: [], // 서버에서 조회한 편집 행
    /** 서버 행에 화면 기본값과 1부터 시작하는 안정적인 그리드 식별값을 붙인다. */
    setHRM2040_R01List: list => set({
        HRM2040_R01List: list.map((item, index) => ({
            ...defaultHRM2040_R01Dto, ...item, // 누락된 화면 필드만 기본값으로 채운다.
            valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1, // 정렬 후 다시 저장할 때 기존 식별값을 유지한다.
            orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
            orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
            isModified: item.isModified ?? false, // 서버 응답에는 화면 편집 표시가 없으므로 false로 시작한다.
        })),
    }),
    /** valueIndex에 해당하는 행만 편집한 행으로 교체한다. */
    updateHRM2040_R01List: (index, data) => set(state => ({
        HRM2040_R01List: state.HRM2040_R01List.map(item => item.valueIndex === index ? data : item),
    })),
    originalHRM2040_R01List: [], // 직전 서버 응답 원본
    /** 조회 목록에 동일한 식별값을 부여해 비교용 원본으로 보관한다. */
    setOriginalHRM2040_R01List: list => set({
        originalHRM2040_R01List: list.map((item, index) => ({
            ...defaultHRM2040_R01Dto, ...item, // 원본도 화면 필드를 누락 없이 갖는다.
            valueIndex: index + 1,
            orderIndex: index + 1,
            orderIndexSeq: 0,
            isModified: false,
        })),
    }),
    selectedHRM2040_R01List: [], // 현재 선택한 행 식별값
    /** 선택 목록을 전달된 문자열 목록으로 교체한다. */
    setSelectedHRM2040_R01List: ids => set({ selectedHRM2040_R01List: ids }),
    /** 새 선택 식별값을 중복 없이 합친다. */
    addSelectedHRM2040_R01List: ids => set(state => ({
        selectedHRM2040_R01List: Array.from(new Set([...state.selectedHRM2040_R01List, ...ids])),
    })),
    /** 전달된 선택 식별값만 제거한다. */
    removeSelectedHRM2040_R01List: ids => set(state => ({
        selectedHRM2040_R01List: state.selectedHRM2040_R01List.filter(id => !ids.includes(id)),
    })),
    /** 선택 목록을 비운다. */
    initSelectedHRM2040_R01List: () => set({ selectedHRM2040_R01List: [] }),
    /** 현재 목록의 모든 valueIndex를 문자열로 선택한다. */
    allSelectedHRM2040_R01List: () => set(state => ({
        selectedHRM2040_R01List: state.HRM2040_R01List.map(item => String(item.valueIndex)),
    })),
    columnState: null, // 사용자 컬럼 배치
    /** 그리드 컬럼 상태를 저장한다. */
    setColumnState: columnState => set({ columnState }),
    scrollTop: null, // 사용자 스크롤 위치
    /** 그리드 스크롤 위치를 저장한다. */
    setScrollTop: scrollTop => set({ scrollTop }),
    /** 이번 달 기본조건과 빈 목록·선택·컬럼·스크롤 상태로 되돌린다. */
    initData: () => set({
        HRM2040_R01Filter: { workYm: formatLocalYearMonth(new Date()), compCd: '', orgCd: '' },
        HRM2040_R01PastFilter: null,
        HRM2040_R01List: [],
        originalHRM2040_R01List: [],
        selectedHRM2040_R01List: [],
        columnState: null,
        scrollTop: null,
    }),
}));
